/**
 * Types and functions for rendering rich text fields with embedded entries from Contentful.
 */

import { BLOCKS } from "@contentful/rich-text-types"
import { documentToReactComponents, NodeRenderer, Options, RenderNode } from "@contentful/rich-text-react-renderer"
import React from "react"
import { ContentfulLivePreview } from "@contentful/live-preview"
import { Asset, Entry, RichTextField } from "../../graphql/contentful/types"
import H1 from "../../components/atoms/typography/H1"
import H2 from "../../components/atoms/typography/H2"
import H3 from "../../components/atoms/typography/H3"
import Body from "../../components/atoms/typography/Body"
import H4 from "../../components/atoms/typography/H4"
import BodyMedium from "../../components/atoms/typography/BodyMedium"
import H0 from "../../components/atoms/typography/H0"
import image from "../../components/contentful/Image"
import PageContentWrapper from "../../components/organisms/PageContentWrapper"

type Component<P> = (props: P) => React.ReactElement | null

export type EntryComponent<P extends Entry> = Component<P>

export type EntryComponentMap<T extends string> = {
  [typename in T]: EntryComponent<Entry<typename>>
}

/**
 * Turns a list of entries into a map.
 * @returns map of entries, indexed by their ids
 */
function entryMap<T extends Entry>(entries: T[]) {
  const map = new Map<string, T>()
  entries.forEach((entry) => map.set(entry.sys?.id!, entry))
  return map
}

const renderAsset =
  (assetMap: Map<string, Asset>, assetComponent: EntryComponent<Asset>): NodeRenderer =>
  (node) => {
    const asset = assetMap.get(node.data.target.sys.id)

    if (!asset) {
      return
    }

    const Image = assetComponent
    return (
      <PageContentWrapper>
        <Image {...asset} {...ContentfulLivePreview.getProps({ assetId: asset.sys?.id!, fieldId: "file" })} />
      </PageContentWrapper>
    )
  }

const renderHeading =
  (component: Component<React.PropsWithChildren>): NodeRenderer =>
  (_node, children) => {
    if (component) {
      const Textblock = component
      return (
        <PageContentWrapper>
          <Textblock>{children}</Textblock>
        </PageContentWrapper>
      )
    }
  }

const renderEntry =
  <T extends string>(entryMap: Map<string, Entry<T>>, entries: EntryComponentMap<T>): NodeRenderer =>
  (node) => {
    const entry = entryMap.get(node.data.target.sys.id)

    if (!entry) {
      return
    }

    const Entry: EntryComponent<Entry<T>> = entries?.[entry?.__typename]

    if (!Entry) {
      return
    }

    return <Entry {...entry} />
  }

const renderList: (component: Component<React.PropsWithChildren>) => NodeRenderer =
  (component) => (_node, children) => {
    const List = component
    return (
      <PageContentWrapper>
        <List>{children}</List>
      </PageContentWrapper>
    )
  }

function renderNode<T extends string>(
  linkedEntries: RichTextField<Entry<T>>["links"],
  entryTypes: EntryComponentMap<T>,
): RenderNode {
  const assetBlockMap = entryMap(linkedEntries.assets.block)
  const entryBlockMap = entryMap(linkedEntries.entries.block)

  return {
    [BLOCKS.EMBEDDED_ENTRY]: renderEntry(entryBlockMap, entryTypes),
    [BLOCKS.EMBEDDED_ASSET]: renderAsset(assetBlockMap, image),
    [BLOCKS.HEADING_1]: renderHeading(H0),
    [BLOCKS.HEADING_2]: renderHeading(H1),
    [BLOCKS.HEADING_3]: renderHeading(H2),
    [BLOCKS.HEADING_4]: renderHeading(H3),
    [BLOCKS.HEADING_5]: renderHeading(H4),
    [BLOCKS.HEADING_6]: renderHeading(BodyMedium),
    [BLOCKS.PARAGRAPH]: renderHeading(Body),
    [BLOCKS.OL_LIST]: renderList((props: React.PropsWithChildren) => <ol>{props.children}</ol>),
    [BLOCKS.UL_LIST]: renderList((props: React.PropsWithChildren) => <ul>{props.children}</ul>),
  }
}

function renderOptions<T extends string>(
  linkedEntries: RichTextField<Entry<T>>["links"],
  entryTypes: EntryComponentMap<T>,
): Options {
  return {
    renderNode: renderNode(linkedEntries, entryTypes),
    preserveWhitespace: true,
  }
}

/**
 * Renders a rich text field as a react element, by mapping nodes in the RTF document tree to react components.
 * @param rtf the rich text data
 * @param entries react component to map to
 */
export function renderRichTextField<T extends string>(
  rtf: RichTextField<Entry<T>>,
  entries: EntryComponentMap<T>,
): React.ReactNode {
  const options = renderOptions<T>(rtf.links, entries)
  return documentToReactComponents(rtf.json, options)
}
